import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { BellOff } from "lucide-react";import { api, gbp } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SEO } from "../components/SEO";

export default function AccountPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [wa, setWa] = useState({ subscribed: false, phone: "" });
  const [waInput, setWaInput] = useState("");

  useEffect(() => {
    if (user === false) navigate("/login", { state: { from: "/account" } });
    if (user) {
      api.get("/orders/me").then(({ data }) => setOrders(data));
      api.get("/watchlist/deals").then(({ data }) => setAlerts(data));
      api.get("/whatsapp/me").then(({ data }) => setWa(data));
    }
  }, [user, navigate]);

  const waSubscribe = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/whatsapp/subscribe", { phone: waInput });
      setWa({ subscribed: true, phone: data.phone });
      setWaInput("");
      toast.success("WhatsApp alerts on. Daily hero deal, straight to your phone.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid number");
    }
  };

  const waUnsubscribe = async () => {
    await api.post("/whatsapp/unsubscribe");
    setWa({ subscribed: false, phone: "" });
    toast.success("WhatsApp alerts off.");
  };

  const removeAlert = async (deal) => {
    await api.post(`/deals/${deal.id}/watch`);
    setAlerts((prev) => prev.filter((d) => d.id !== deal.id));
    toast.success("Alert removed.");
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <div className="text-[10px] uppercase tracking-widest text-[#FFCC00] mb-2">UK JobLot Club member</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-10">{user.name}<span className="text-[#FF3B30]">.</span></h1>
      <SEO title="My Account" />

      <div className="border border-[#27272A] p-6 mb-12" data-testid="whatsapp-alerts-panel">
        <h2 className="font-display text-2xl mb-2">WHATSAPP DEAL ALERTS</h2>
        {wa.subscribed ? (
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-white/50 text-sm">Daily hero deal drops go to <span className="text-[#FFCC00]">{wa.phone}</span>.</p>
            <button data-testid="whatsapp-unsubscribe-button" onClick={waUnsubscribe} className="btn-ghost-industrial px-4 py-2 text-sm">TURN OFF</button>
          </div>
        ) : (
          <form onSubmit={waSubscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="whatsapp-phone-input"
              required
              placeholder="MOBILE — E.G. 07700 900123"
              value={waInput}
              onChange={(e) => setWaInput(e.target.value)}
              className="flex-1 bg-transparent border border-[#27272A] px-4 py-3 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30"
            />
            <button data-testid="whatsapp-subscribe-button" className="btn-industrial px-6 py-3 text-lg">GET WHATSAPP ALERTS</button>
          </form>
        )}
      </div>

      <h2 className="font-display text-2xl mb-6">MY DEAL ALERTS</h2>
      {alerts.length === 0 ? (
        <div className="border border-[#27272A] p-8 text-center mb-12" data-testid="alerts-empty-state">
          <div className="font-display text-xl text-white/30 mb-3">NO ALERTS SET.</div>
          <Link to="/coming-soon" data-testid="alerts-browse-link" className="text-[#FFCC00] text-xs uppercase tracking-widest border-b border-[#FFCC00] hover:text-white hover:border-white">
            Browse coming-soon drops and tap Notify Me →
          </Link>
        </div>
      ) : (
        <div className="space-y-px bg-[#27272A] border border-[#27272A] mb-12" data-testid="alerts-list">
          {alerts.map((d) => (
            <div key={d.id} data-testid={`alert-row-${d.slug}`} className="bg-[#0A0A0A] p-5 flex flex-wrap gap-5 items-center">
              <img src={d.images[0]} alt={d.title} className="w-16 h-16 object-cover grayscale" />
              <div className="flex-1 min-w-[180px]">
                <Link to={`/deal/${d.slug}`} className="font-display text-lg leading-tight hover:text-[#FFCC00] block">{d.title}</Link>
                <div className="text-white/40 text-xs mt-1">
                  {d.sku} · {d.condition} ·{" "}
                  <span className={d.status === "live" ? "text-[#FFCC00]" : "text-[#FF3B30]"}>{d.status === "live" ? "LIVE NOW" : "COMING SOON"}</span>
                </div>
              </div>
              <button data-testid={`alert-remove-${d.slug}`} onClick={() => removeAlert(d)} className="btn-ghost-industrial px-4 py-2 text-sm flex items-center gap-2">
                <BellOff size={14} /> REMOVE
              </button>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display text-2xl mb-6">MY ORDERS</h2>
      {orders.length === 0 ? (
        <div className="border border-[#27272A] p-12 text-center font-display text-2xl text-white/30">NO ORDERS YET.</div>
      ) : (
        <div className="space-y-px bg-[#27272A] border border-[#27272A]">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.reference}`} className="bg-[#0A0A0A] p-6">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div>
                  <div className="font-display text-2xl text-[#FFCC00]">{o.reference}</div>
                  <div className="text-white/40 text-xs mt-1">{new Date(o.created_at).toLocaleString("en-GB")}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl">{gbp(o.total)}</div>
                  <div className={`text-[10px] uppercase tracking-widest mt-1 ${o.status === "awaiting_payment" ? "text-[#FF3B30]" : "text-[#FFCC00]"}`}>
                    {o.status.replace("_", " ")}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#27272A] text-xs text-white/50 space-y-1">
                {o.items.map((i, idx) => (
                  <div key={idx}>{i.qty}x {i.title} — {gbp(i.line_total)}</div>
                ))}
                <div className="text-white/30">{o.delivery_method === "collection" ? "Warehouse collection" : `Pallet delivery to ${o.postcode}`}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
